import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { authApi } from '../services/api'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(() => {
    try { return JSON.parse(localStorage.getItem('user')) } catch { return null }
  })
  const [loading, setLoading] = useState(true)

  // Verify token on mount
  useEffect(() => {
    const token = localStorage.getItem('token')
    if (!token) { setLoading(false); return }

    authApi.me()
      .then(({ data }) => setUser(data.user))
      .catch(() => { localStorage.removeItem('token'); localStorage.removeItem('user') })
      .finally(() => setLoading(false))
  }, [])

  // Possible return shapes:
  //   { twoFactorRequired: true }       - account has TOTP 2FA, prompt for code
  //   { requiresVerification: true, email, devOtp? } - email OTP not yet completed
  //   { user }                          - happy path, token persisted
  const login = useCallback(async (email, password, code) => {
    try {
      const { data } = await authApi.login({ email, password, code })
      if (data.two_factor_required) {
        return { twoFactorRequired: true }
      }
      localStorage.setItem('token', data.token)
      localStorage.setItem('user', JSON.stringify(data.user))
      setUser(data.user)
      return { user: data.user }
    } catch (err) {
      // Backend signals "email not verified yet" with a 403 carrying
      // requires_verification. We hand that back to the caller so the
      // Login page can route into the verify-otp flow without surfacing
      // a misleading error to the user.
      if (
        err.response?.status === 403 &&
        err.response?.data?.requires_verification
      ) {
        return {
          requiresVerification: true,
          email:                err.response.data.email,
          devOtp:               err.response.data.dev_otp ?? null,
        }
      }
      throw err
    }
  }, [])

  // Register no longer mints a token on success - the server gates new
  // accounts behind email-OTP verification. We return the verification
  // payload so the page can navigate to /verify-otp and pass the dev_otp
  // hint along.
  const register = useCallback(async (payload) => {
    const { data } = await authApi.register(payload)
    return {
      requiresVerification: !!data.requires_verification,
      email:                data.email,
      devOtp:               data.dev_otp ?? null,
    }
  }, [])

  const logout = useCallback(async () => {
    try { await authApi.logout() } catch {}
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    setUser(null)
  }, [])

  const updateUser = useCallback((updated) => {
    setUser(updated)
    localStorage.setItem('user', JSON.stringify(updated))
  }, [])

  // QA round 7 - refresh /me so plan_limits counters (facilities_used,
  // units_used) update after a Facility / Unit create.
  const refreshMe = useCallback(async () => {
    try {
      const { data } = await authApi.me()
      setUser(data.user)
      localStorage.setItem('user', JSON.stringify(data.user))
      return data.user
    } catch { return null }
  }, [])

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, updateUser, refreshMe }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
